<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from 'vue'
import MsToast from './toast.vue'
import { TOAST_DISMISS_KEY, dismissToast, toasts, type ToastEntry } from './use-toast'

defineOptions({ name: 'MsToaster' })

withDefaults(defineProps<{ tone?: 'inverted' | 'surface' }>(), { tone: 'inverted' })

// Mounted once, near the root.

const expanded = ref(false)

// Opening is immediate; closing waits a moment and can be called off.
let collapseTimer: ReturnType<typeof setTimeout> | undefined

const openStack = (): void => {
  clearTimeout(collapseTimer)
  collapseTimer = undefined
  expanded.value = true
}

const scheduleCollapse = (): void => {
  clearTimeout(collapseTimer)
  collapseTimer = setTimeout(() => {
    expanded.value = false
  }, 180)
}

// Toasts on their way out.
const leaving = ref<number[]>([])

const leaveMs = (): number => {
  const raw = getComputedStyle(document.body).getPropertyValue('--toast-leave-ms')
  return Number.parseFloat(raw) || 0
}

const dismiss = (id: number): void => {
  if (leaving.value.includes(id)) return
  clearTimer(id)
  leaving.value = [...leaving.value, id]
  setTimeout(() => {
    leaving.value = leaving.value.filter((entry) => entry !== id)
    dismissToast(id)
  }, leaveMs() + 20)
}

// The close button and the timers both go through the host, so both animate.
provide(TOAST_DISMISS_KEY, dismiss)

/** Everything still counted for position — the leaving ones no longer are. */
const live = computed(() => toasts.value.filter((entry) => !leaving.value.includes(entry.id)))

const heights = ref<Record<number, number>>({})
const elements = new Map<number, HTMLElement>()

const setElement = (id: number, el: unknown): void => {
  if (el instanceof HTMLElement) elements.set(id, el)
  else elements.delete(id)
}

const measure = (): void => {
  const next: Record<number, number> = {}
  for (const entry of toasts.value) {
    const el = elements.get(entry.id)
    if (el) next[entry.id] = el.offsetHeight
  }
  heights.value = next
}

const timers = new Map<number, ReturnType<typeof setTimeout>>()

const clearTimer = (id: number): void => {
  const timer = timers.get(id)
  if (timer) clearTimeout(timer)
  timers.delete(id)
}

const startTimer = (entry: ToastEntry): void => {
  clearTimer(entry.id)
  if (entry.duration === null) return
  timers.set(
    entry.id,
    setTimeout(() => dismiss(entry.id), entry.duration),
  )
}

const syncTimers = (): void => {
  const live = new Set(toasts.value.map((entry) => entry.id))
  for (const id of [...timers.keys()]) if (!live.has(id)) clearTimer(id)
  if (expanded.value) {
    for (const id of [...timers.keys()]) clearTimer(id)
    return
  }
  for (const entry of toasts.value) if (!timers.has(entry.id)) startTimer(entry)
}

watch(
  () => toasts.value.map((entry) => `${entry.id}:${entry.revision}`).join(','),
  async () => {
    for (const entry of toasts.value) {
      const timer = timers.get(entry.id)
      if (timer && entry.duration === null) clearTimer(entry.id)
    }
    syncTimers()
    await nextTick()
    measure()
  },
  { immediate: true },
)

watch(expanded, () => {
  syncTimers()
  if (!expanded.value) for (const entry of toasts.value) startTimer(entry)
})

onBeforeUnmount(() => {
  for (const id of [...timers.keys()]) clearTimer(id)
  clearTimeout(collapseTimer)
})

const frontHeight = computed(() => {
  const front = live.value[0]
  return front ? (heights.value[front.id] ?? 0) : 0
})

const styleFor = (entry: ToastEntry): Record<string, string> => {
  // On its way out: down and faded, wherever it happened to be.
  if (leaving.value.includes(entry.id)) {
    return {
      transform: 'translateY(100%) scale(0.96)',
      opacity: '0',
      zIndex: '0',
      pointerEvents: 'none',
      blockSize: 'auto',
      overflow: 'visible',
      transitionDuration: 'var(--toast-leave-duration)',
      transitionTimingFunction: 'var(--toast-leave-ease)',
    }
  }

  const index = live.value.findIndex((candidate) => candidate.id === entry.id)

  if (expanded.value) {
    let offset = 0
    for (let i = 0; i < index; i += 1) {
      const behind = live.value[i]
      offset += (behind ? (heights.value[behind.id] ?? 0) : 0) + 8
    }
    return {
      transform: `translateY(-${offset}px) scale(1)`,
      zIndex: String(100 - index),
      opacity: '1',
      pointerEvents: 'auto',
      blockSize: 'auto',
      overflow: 'visible',
    }
  }
  return {
    blockSize: index === 0 || !frontHeight.value ? 'auto' : `${frontHeight.value}px`,
    // Only the ones behind are clipped, and only while the stack is closed.
    overflow: index === 0 ? 'visible' : 'hidden',
    transform: `translateY(calc(-1 * ${index} * var(--toast-peek))) scale(calc(1 - ${index} * var(--toast-scale-step)))`,
    zIndex: String(100 - index),
    opacity: index < 3 ? '1' : '0',
    pointerEvents: index === 0 ? 'auto' : 'none',
  }
}

const hasToasts = computed(() => toasts.value.length > 0)

const hostEl = ref<HTMLElement | null>(null)

const canPopover =
  typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype

const popoverMode = canPopover ? 'manual' : undefined

const promote = (): void => {
  const el = hostEl.value
  if (!canPopover || !el?.isConnected) return
  try {
    if (el.matches(':popover-open')) el.hidePopover()
    el.showPopover()
  } catch {
    /* Not connected yet, or the selector is unknown. The toaster still draws;
       it just draws where a z-index can reach. */
  }
}

watch(
  () => toasts.value.length,
  (count) => {
    if (!canPopover) return
    if (count) {
      void nextTick(promote)
      return
    }
    try {
      hostEl.value?.hidePopover()
    } catch {
      /* Already closed. */
    }
  },
  { flush: 'post' },
)
</script>

<template>
  <div
    v-show="hasToasts"
    ref="hostEl"
    :popover="popoverMode"
    class="ms-toaster"
    :class="[`ms-toaster--${tone}`, { 'ms-toaster--expanded': expanded }]"
    role="region"
    aria-label="Notifications"
    aria-live="polite"
    :aria-relevant="'additions'"
    @pointerenter="openStack"
    @pointerleave="scheduleCollapse"
    @focusin="openStack"
    @focusout="scheduleCollapse"
  >
    <div
      v-for="entry in toasts"
      :key="entry.id"
      :ref="(el) => setElement(entry.id, el)"
      class="ms-toaster__slot"
      :style="styleFor(entry)"
    >
      <MsToast :entry="entry" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ms-toaster {
  position: fixed;
  inset-block-end: var(--toast-viewport-inset);
  inset-block-start: auto;
  inset-inline-end: auto;
  margin: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  overflow: visible;
  inset-inline-start: 50%;
  inline-size: min(var(--toast-width), calc(100vw - 2 * var(--toast-viewport-gutter)));
  transform: translateX(-50%);
  z-index: var(--toast-z);

  block-size: 0;
  pointer-events: none;

  &--surface {
    --toast-bg: var(--toast-surface-bg);
    --toast-border: var(--toast-surface-border);
    --toast-fg: var(--toast-surface-fg);
    --toast-description-fg: var(--toast-surface-description-fg);
    --toast-success-fg: var(--toast-surface-success-fg);
    --toast-info-fg: var(--toast-surface-info-fg);
    --toast-warning-fg: var(--toast-surface-warning-fg);
    --toast-error-fg: var(--toast-surface-error-fg);
    --toast-close-fg: var(--toast-surface-close-fg);
    --toast-close-fg-hover: var(--toast-surface-close-fg-hover);
    --toast-close-bg-hover: var(--toast-surface-close-bg-hover);
  }

  &__slot {
    position: absolute;
    inset-block-end: 0;
    inset-inline: 0;
    transform-origin: bottom center;
    transition:
      transform var(--toast-move-duration) var(--toast-enter-ease),
      opacity var(--toast-move-duration) var(--toast-enter-ease);
    will-change: transform;
  }
}

/* Entering and leaving. */
.ms-toaster__slot {
  animation: ms-toast-in var(--toast-enter-duration) var(--toast-enter-ease);
}

@keyframes ms-toast-in {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ms-toaster__slot {
    animation: none;
  }
}
</style>
